"use client";

import { useMemo, useCallback, useRef, useEffect, useState, type CSSProperties } from "react";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePipelineJob } from "@/features/jobs/context/pipeline-job-context";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";

/** Minimal shape `StageCarouselNav` needs — duck-typed so callers (pipeline's
 * `PipelineStage`, the Arbitrator's local `Stage`) can pass their own stage
 * type without a shared import. */
export interface StageCarouselStage {
  id: string;
  name: string;
  color?: string | null;
  candidate_count?: number;
}

/**
 * Mobile "step through stages one at a time" affordance: a prev/next chevron
 * pair around a single stage chip. Extracted from `StageBar`'s mobile layout
 * so the Job Arbitrator's stage bar (`features/arbitrator/components/results/
 * arbitrator-stage-bar.tsx`) can reuse the exact same affordance instead of
 * forking it (ARBI-666) — the Arbitrator has no "All" tab, so only this
 * carousel portion (not the "Stages" label / "All" chip wrapper) is shared.
 */
export function StageCarouselNav({
  stages,
  selectedStageId,
  onSelect,
}: {
  stages: StageCarouselStage[];
  selectedStageId: string | null;
  onSelect: (stageId: string) => void;
}) {
  const carouselIndex = useMemo(() => {
    const idx = stages.findIndex((s) => s.id === selectedStageId);
    return idx >= 0 ? idx : 0;
  }, [stages, selectedStageId]);

  const goBack = useCallback(() => {
    const prev = stages[carouselIndex - 1];
    if (prev) onSelect(prev.id);
  }, [stages, carouselIndex, onSelect]);

  const goNext = useCallback(() => {
    const next = stages[carouselIndex + 1];
    if (next) onSelect(next.id);
  }, [stages, carouselIndex, onSelect]);

  const carouselStage = stages[carouselIndex] ?? null;

  if (stages.length === 0) return null;

  return (
    <div className="flex items-center gap-1 min-w-0">
      <button
        type="button"
        aria-label="Previous stage"
        onClick={goBack}
        disabled={carouselIndex === 0}
        className="flex size-7 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-contrast/6 disabled:opacity-30 disabled:cursor-default cursor-pointer"
      >
        <Icon name="IconChevronLeft" size={14} className="text-contrast/70" />
      </button>
      {carouselStage && (
        <StageChip
          label={carouselStage.name}
          count={carouselStage.candidate_count ?? 0}
          isSelected={selectedStageId === carouselStage.id}
          color={carouselStage.color}
          onClick={() => onSelect(carouselStage.id)}
        />
      )}
      <button
        type="button"
        aria-label="Next stage"
        onClick={goNext}
        disabled={carouselIndex === stages.length - 1}
        className="flex size-7 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-contrast/6 disabled:opacity-30 disabled:cursor-default cursor-pointer"
      >
        <Icon name="IconChevronRight" size={14} className="text-contrast/70" />
      </button>
    </div>
  );
}

export function StageBar() {
  const { stages } = usePipelineJob();
  const { selectedStageId, setSelectedStageId } = usePipelineViewStore();

  const sortedStages = useMemo(
    () => [...stages].sort((a, b) => a.position - b.position),
    [stages],
  );

  const { sourcedStage, middleStages, archivedStage } = useMemo(() => {
    if (sortedStages.length === 0) {
      return { sourcedStage: null, middleStages: [], archivedStage: null };
    }
    const sourced =
      sortedStages.find((s) => s.is_system && s.name === "Sourced") ?? null;
    const maxPos = Math.max(...sortedStages.map((s) => s.position));
    const archived =
      sortedStages.find((s) => s.is_system && s.position === maxPos) ?? null;
    const middle = sortedStages.filter((s) => s !== sourced && s !== archived);
    return {
      sourcedStage: sourced,
      middleStages: middle,
      archivedStage: archived,
    };
  }, [sortedStages]);

  const allCount = useMemo(
    () => stages.reduce((sum, s) => sum + (s.candidate_count ?? 0), 0),
    [stages],
  );

  const viewportRef = useRef<HTMLDivElement>(null);
  const [showLeftGrad, setShowLeftGrad] = useState(false);
  const [showRightGrad, setShowRightGrad] = useState(false);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => {
      setShowLeftGrad(el.scrollLeft > 0);
      setShowRightGrad(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      data-tour="pipe-stages"
      className="bg-contrast/[0.02] border-b border-border flex shrink-0"
    >
      {/* Mobile layout */}
      <div className="flex md:hidden items-center justify-between w-full gap-3 px-4 py-3">
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-base font-medium text-heading whitespace-nowrap">
            Stages
          </span>
          <StageChip
            label="All"
            count={allCount}
            isSelected={selectedStageId === null}
            color="#FFAF53"
            onClick={() => setSelectedStageId(null)}
          />
        </div>

        <StageCarouselNav
          stages={sortedStages}
          selectedStageId={selectedStageId}
          onSelect={setSelectedStageId}
        />
      </div>

      {/* Desktop layout */}
      {/* Desktop layout — full-width scroll area with fade gradients */}
      <div className="hidden md:flex flex-1 min-w-0 relative">
        <ScrollArea className="flex-1 min-w-0" viewportRef={viewportRef}>
          <div className="flex min-w-full items-center gap-3 px-4 py-3">
            <span className="text-base font-medium text-heading whitespace-nowrap shrink-0">
              Stages
            </span>

            <StageChip
              label="All"
              count={allCount}
              isSelected={selectedStageId === null}
              color="#FFAF53"
              onClick={() => setSelectedStageId(null)}
            />

            <div className="w-px h-[25px] bg-contrast/10 shrink-0" />

            {sourcedStage && (
              <StageChip
                label={sourcedStage.name}
                count={sourcedStage.candidate_count ?? 0}
                isSelected={selectedStageId === sourcedStage.id}
                color={sourcedStage.color}
                onClick={() => setSelectedStageId(sourcedStage.id)}
              />
            )}

            {middleStages.map((stage) => (
              <div key={stage.id} className="flex items-center gap-3 shrink-0">
                <div className="opacity-40">
                  <Icon name="IconArrowRight" size={16} className="text-foreground" />
                </div>
                <StageChip
                  label={stage.name}
                  count={stage.candidate_count ?? 0}
                  isSelected={selectedStageId === stage.id}
                  color={stage.color}
                  onClick={() => setSelectedStageId(stage.id)}
                />
              </div>
            ))}

            {archivedStage && (
              <div className="ml-auto flex items-center gap-3 shrink-0">
                <div className="w-px h-[25px] bg-contrast/10 shrink-0" />
                <StageChip
                  label={archivedStage.name}
                  count={archivedStage.candidate_count ?? 0}
                  isSelected={selectedStageId === archivedStage.id}
                  color={archivedStage.color}
                  onClick={() => setSelectedStageId(archivedStage.id)}
                />
              </div>
            )}
          </div>
        </ScrollArea>

        {showLeftGrad && (
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-16 z-10"
            style={{ background: "linear-gradient(to right, var(--surface-primary), transparent)" }}
          />
        )}
        {showRightGrad && (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-16 z-10"
            style={{ background: "linear-gradient(to left, var(--surface-primary), transparent)" }}
          />
        )}
      </div>
    </div>
  );
}

function StageChip({
  label,
  count,
  isSelected,
  color,
  onClick,
}: {
  label: string;
  count: number;
  isSelected: boolean;
  color?: string | null;
  onClick: () => void;
}) {
  return (
    <Button
      variant="linear"
      size="sm"
      onClick={onClick}
      className="shrink-0"
      style={
        { "--gradient-color": isSelected ? color : "#6b7280" } as CSSProperties
      }
    >
      <span>{label}</span>
      <span className="text-foreground/50">({count})</span>
    </Button>
  );
}
